import React from "react";
import styled, { keyframes, css } from "styled-components";
import { Outlet, useLocation } from "react-router-dom";
import CompanyLogo from "../../molecules/Logo/CompanyLogo";

import facebookIcon from "../../../assets/Icons/login/facebook.webp";
import instaIcon from "../../../assets/Icons/login/instagram.webp";
import loginCollage from "../../../assets/images/SignupPageImages/login_images.webp";

const SUPPORT_WHATSAPP = "https://wa.me/919875082248";

const kenBurns = keyframes`
  0% { transform: scale(1) translate(0, 0); }
  100% { transform: scale(1.08) translate(-1.5%, -1%); }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-36px); }
  to { opacity: 1; transform: translateX(0); }
`;

const floatA = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const floatB = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
`;

const floatC = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const AuthContainer = styled.div`
  width: 100%;
  min-height: 100dvh;
  position: relative;
  overflow: hidden;
  background: #0c0b0d;
  display: grid;
  grid-template-columns: minmax(360px, 0.95fr) 1.15fr;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const VisualStage = styled.aside`
  position: relative;
  min-height: 100dvh;
  overflow: hidden;
  order: 2;

  @media (max-width: 960px) {
    display: none;
  }
`;

const StageImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  animation: ${kenBurns} 22s ease-in-out infinite alternate;
  will-change: transform;
`;

const StageScrim = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(12, 11, 13, 0.92) 0%, rgba(12, 11, 13, 0.35) 28%, rgba(12, 11, 13, 0.15) 55%, transparent 100%),
    linear-gradient(180deg, rgba(12, 11, 13, 0.45) 0%, transparent 28%, transparent 70%, rgba(12, 11, 13, 0.7) 100%),
    linear-gradient(135deg, rgba(255, 201, 81, 0.12) 0%, transparent 40%, rgba(206, 86, 176, 0.18) 100%);
  z-index: 1;
  pointer-events: none;
`;

const StageCopy = styled.div`
  position: absolute;
  left: 8%;
  bottom: 10%;
  z-index: 2;
  max-width: 420px;
  animation: ${riseIn} 0.9s ease-out 0.25s both;
`;

const StageEyebrow = styled.p`
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #ffc951;
  margin: 0 0 14px;
`;

const StageHeadline = styled.h1`
  font-family: "Urbanist", sans-serif;
  font-size: clamp(36px, 4.2vw, 56px);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: #ffffff;
  margin: 0 0 14px;
  text-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
`;

const StageAccent = styled.span`
  background: linear-gradient(120deg, #ffc951 0%, #ce56b0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StageSub = styled.p`
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.78);
  margin: 0;
  max-width: 340px;
`;

const FilmEdge = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 28px;
  z-index: 3;
  background: #0c0b0d;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  padding: 24px 0;

  span {
    width: 14px;
    height: 18px;
    border-radius: 3px;
    background: #1a191c;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  }
`;

const FloatingTag = styled.div<{
  $top: string;
  $left?: string;
  $right?: string;
  $delay?: string;
  $motion?: "a" | "b" | "c";
}>`
  position: absolute;
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left || "auto"};
  right: ${({ $right }) => $right || "auto"};
  z-index: 4;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: rgba(255, 255, 255, 0.92);
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  white-space: nowrap;
  pointer-events: none;
  animation: ${riseIn} 0.7s ease-out ${({ $delay }) => $delay || "0.35s"} both,
    ${({ $motion }) =>
    $motion === "b"
      ? css`${floatB}`
      : $motion === "c"
        ? css`${floatC}`
        : css`${floatA}`}
      ${({ $motion }) => ($motion === "b" ? "5.5s" : $motion === "c" ? "6s" : "4.8s")}
      ease-in-out ${({ $delay }) => $delay || "0.35s"} infinite;
`;

const TagDot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 2px ${({ $color }) => `${$color}28`};
  flex-shrink: 0;
  opacity: 0.9;
`;

const FormColumn = styled.section`
  position: relative;
  z-index: 4;
  order: 1;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 48px 36px;
  background: #0c0b0d;
  animation: ${slideFromLeft} 0.7s ease-out both;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: -80px;
    width: 160px;
    height: 100%;
    background: linear-gradient(90deg, #0c0b0d 0%, transparent 100%);
    pointer-events: none;
    z-index: 0;

    @media (max-width: 960px) {
      display: none;
    }
  }

  @media (max-width: 960px) {
    padding: 28px 22px 32px;
    background:
      radial-gradient(ellipse 90% 60% at 100% 0%, rgba(206, 86, 176, 0.22) 0%, transparent 55%),
      radial-gradient(ellipse 70% 50% at 0% 100%, rgba(255, 201, 81, 0.14) 0%, transparent 50%),
      #0c0b0d;
  }
`;

const FormInner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
  flex: 1;
  justify-content: center;

  @media (min-width: 1440px) {
    max-width: 560px;
    gap: 32px;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  span {
    color: #ffffff !important;
  }
`;

const FormSlot = styled.div`
  width: 100%;
  overflow: visible;
  position: relative;
  z-index: 5;
`;

const BottomBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 201, 81, 0.22);
  box-shadow:
    0 0 0 1px rgba(206, 86, 176, 0.08),
    0 10px 28px rgba(0, 0, 0, 0.28);
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const SocialIcon = styled.a`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(
    145deg,
    rgba(255, 201, 81, 0.22) 0%,
    rgba(206, 86, 176, 0.28) 100%
  );
  border: 1px solid rgba(255, 201, 81, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 0 0 3px rgba(206, 86, 176, 0.14),
    0 6px 16px rgba(0, 0, 0, 0.28);
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px) scale(1.05);
    border-color: rgba(255, 201, 81, 0.75);
    box-shadow:
      0 0 0 4px rgba(255, 201, 81, 0.2),
      0 10px 22px rgba(206, 86, 176, 0.32);
  }

  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
    opacity: 1;
  }
`;

const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 999px;
  background: linear-gradient(144.01deg, rgba(255, 201, 81) 0%, rgba(206, 86, 176) 100%);
  color: #ffffff;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.02em;
  text-decoration: none;
  box-shadow: 0 8px 20px rgba(206, 86, 176, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.05);
    box-shadow: 0 12px 26px rgba(206, 86, 176, 0.5);
  }
`;

const ContactIcon = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 11px;
    height: 11px;
    fill: #ffffff;
  }
`;

const RouteHint = styled.p`
  font-family: "Urbanist", sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
`;

const MobileBrandStrip = styled.div`
  display: none;

  @media (max-width: 960px) {
    display: block;
    margin-bottom: 4px;
  }
`;

const MobileHeadline = styled.h1`
  font-family: "Urbanist", sans-serif;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: #fff;
  margin: 0 0 6px;
`;

const MobileSub = styled.p`
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
  line-height: 1.4;
`;

const routeLabel = (pathname: string) => {
  if (pathname.includes("otp")) return "Studio verify · 02";
  if (pathname.includes("signup")) return "Studio setup · 02";
  return "Studio login · 01";
};

const Login: React.FC = () => {
  const { pathname } = useLocation();

  return (
    <AuthContainer>
      <FormColumn>
        <FormInner>
          <TopBar>
            <CompanyLogo />
          </TopBar>

          <MobileBrandStrip>
            <MobileHeadline>
              Built for <span style={{ color: "#ffc951" }}>studio owners</span>
            </MobileHeadline>
            <MobileSub>
              Sign in to run events, galleries, and your studio team.
            </MobileSub>
          </MobileBrandStrip>

          <FormSlot>
            <Outlet />
          </FormSlot>

          <BottomBar>
            <RouteHint>{routeLabel(pathname)}</RouteHint>
            <SocialLinks>
              <ContactButton
                href={SUPPORT_WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Contact support on WhatsApp"
              >
                <ContactIcon>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6.6 10.8c1.4 2.7 3.9 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.2 1.2.4 2.5.6 3.8.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.3 1.1l-2.2 2.9z" />
                  </svg>
                </ContactIcon>
                Contact
              </ContactButton>
              <SocialIcon
                href="https://www.facebook.com/profile.php?id=61578136391260"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <img src={facebookIcon} alt="" />
              </SocialIcon>
              <SocialIcon
                href="https://www.instagram.com/kwikmemories/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <img src={instaIcon} alt="" />
              </SocialIcon>
            </SocialLinks>
          </BottomBar>
        </FormInner>
      </FormColumn>

      <VisualStage>
        <StageImage src={loginCollage} alt="" />
        <StageScrim />
        <FilmEdge aria-hidden="true">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} />
          ))}
        </FilmEdge>

        <FloatingTag $top="16%" $right="8%" $delay="0.35s" $motion="a">
          <TagDot $color="#ce56b0" />
          Event management
        </FloatingTag>
        <FloatingTag $top="28%" $right="18%" $delay="0.55s" $motion="b">
          <TagDot $color="#ffc951" />
          Team & payments
        </FloatingTag>
        <FloatingTag $top="42%" $left="10%" $delay="0.7s" $motion="c">
          <TagDot $color="#e86ab0" />
          Client galleries
        </FloatingTag>

        <StageCopy>
          <StageEyebrow>Admin · Studio Dashboard</StageEyebrow>
          <StageHeadline>
            Run your studio
            <br />
            <StageAccent>from one place.</StageAccent>
          </StageHeadline>
          <StageSub>
            Manage events, client galleries, team payments, and deliveries —
            built for studio owners.
          </StageSub>
        </StageCopy>
      </VisualStage>
    </AuthContainer>
  );
};

export default Login;
